<template>
  <div class="sl-v2">
    <div class="kicker">Prime · Operations</div>
    <h1>Smart Links</h1>
    <p class="sub">Routing performance across all smart links. Expand a row to see its offers.</p>

    <div class="toolbar">
      <label class="ctl search">
        <input v-model="q" type="text" placeholder="Search smart link name or ID…" @input="onSearch">
      </label>
      <div class="ctl">
        <date-range-picker
          v-model="dateRange"
          opens="right"
          :locale-data="{ firstDay: 1, format: 'dd.mm.yyyy' }"
          :ranges="ranges"
          :maxDate="maxDate"
          @toggle="onDateToggle"
        />
      </div>
      <div class="ctl">
        <select v-model="status" @change="fetchRows">
          <option value="">All statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="PAUSED">Paused</option>
        </select>
      </div>
      <div class="ctl">
        <select v-model="groupId" @change="page = 1; fetchRows()">
          <option v-for="g in filterGroups" :key="'fg'+g.id" :value="g.id">{{ g.name }}</option>
        </select>
      </div>
      <label class="switch" title="Rows with fewer than 20 requests are hidden by default">
        <input type="checkbox" v-model="showZeros" @change="fetchRows">
        <span class="track"></span>
        Show zero rows <span class="mut" style="font-size:11px">(&lt;20 req)</span>
      </label>
      <div class="grow"></div>
      <button class="btn primary" type="button" :disabled="loading" @click="fetchRows">
        <span v-if="loading" class="spin"></span>
        {{ loading ? 'Loading…' : 'Refresh' }}
      </button>
    </div>

    <div v-if="loading" class="loadbar">
      <span class="spin lg"></span>
      Loading smart links…
    </div>

    <div class="summary" :class="{ dim: loading }">
      <div class="stat accent"><div class="l">Requests</div><div class="v">{{ fmtNum(summary.requests) }}</div></div>
      <div class="stat"><div class="l">Served</div><div class="v">{{ fmtNum(summary.served) }}</div></div>
      <div class="stat"><div class="l">Lost</div><div class="v">{{ fmtNum(summary.lost) }}</div></div>
      <div class="stat"><div class="l">Lost %</div><div class="v">{{ fmtPct(summary.lost_pct) }}</div></div>
    </div>

    <div class="tablecard" :class="{ 'is-loading': loading }">
      <div v-if="loading" class="tbl-load">
        <span class="spin lg"></span>
        Loading smart links…
      </div>
      <div class="scroll">
        <table>
          <thead>
            <tr>
              <th class="ns" style="width:36px"></th>
              <th class="l" style="width:72px" @click="sortBy('id')">ID</th>
              <th class="l namecol" @click="sortBy('name')">Name</th>
              <th class="l ns statuscol">Status</th>
              <th class="l ns" style="width:150px">Group</th>
              <th @click="sortBy('requests')">Requests</th>
              <th @click="sortBy('served')">Served</th>
              <th @click="sortBy('lost')">Lost</th>
              <th @click="sortBy('lost_pct')">Lost %</th>
            </tr>
          </thead>
          <tbody>
            <tr class="total">
              <td></td>
              <td class="l" colspan="4">Filtered total · {{ summary.visible }} of {{ summary.total }}</td>
              <td>{{ fmtNum(summary.requests) }}</td>
              <td>{{ fmtNum(summary.served) }}</td>
              <td>{{ fmtNum(summary.lost) }}</td>
              <td>{{ fmtPct(summary.lost_pct) }}</td>
            </tr>
            <template v-for="row in rows">
              <tr class="sl" :key="'r'+row.id">
                <td>
                  <button v-if="row.offers && row.offers.length" class="chev" type="button" @click="toggle(row.id)">▸</button>
                </td>
                <td class="l idcell">
                  <a class="nlink" @click.prevent="openWorkspace(row.id)">{{ row.id }}</a>
                </td>
                <td class="l namecol">
                  <a class="nlink" @click.prevent="openWorkspace(row.id)">{{ row.name }}</a>
                </td>
                <td class="l statuscol"><SlV2Status :sl="statusModel(row)" @changed="fetchRows" /></td>
                <td class="l gcell">
                  <select :value="row.group_id" @change="setGroup(row, $event)">
                    <option v-for="g in groupList" :key="'g'+g.id" :value="g.id">{{ g.name }}</option>
                  </select>
                  <div v-if="row.scheduled && !row.forced" class="ghint" :class="row.group_status === 'ACTIVE' ? 'on' : 'off'">
                    Group is {{ row.group_status }}
                  </div>
                </td>
                <td>
                  {{ fmtNum(row.requests) }}
                  <button
                    v-if="userHasPermission('all-requests-report') && row.requests > 0"
                    class="dl"
                    type="button"
                    title="Download requests"
                    @click.stop="generateReport(row.id, 'requests')"
                  >⤓</button>
                </td>
                <td>{{ fmtNum(row.served) }}</td>
                <td>
                  <span class="lostn" @mouseenter="showPop($event, row)" @mouseleave="hidePopSoon">{{ fmtNum(row.lost) }}</span>
                  <button
                    v-if="row.lost > 0"
                    class="dl"
                    type="button"
                    title="Download lost"
                    @click.stop="generateReport(row.id, 'blocked')"
                  >⤓</button>
                </td>
                <td>
                  <span class="lost" :class="lostClass(row.lost_pct)" @mouseenter="showPop($event, row)" @mouseleave="hidePopSoon">{{ fmtPct(row.lost_pct) }}</span>
                </td>
              </tr>
              <tr v-for="o in (open[row.id] ? (row.offers || []) : [])" :key="'o'+o.id" class="child">
                <td></td>
                <td class="l idcell">{{ o.id }}</td>
                <td class="l namecol">{{ o.name }}</td>
                <td class="l mut statuscol">—</td>
                <td class="l mut">—</td>
                <td>{{ fmtNum(o.requests) }}</td>
                <td>{{ fmtNum(o.served) }}</td>
                <td>{{ fmtNum(o.lost) }}</td>
                <td>{{ fmtPct(o.lost_pct) }}</td>
              </tr>
            </template>
            <tr v-if="!loading && rows.length === 0">
              <td colspan="9" class="empty"><b>No smart links match</b>Try clearing filters or showing zero rows.</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="foot">
        <span class="mut">{{ total }} rows · page {{ page }}</span>
        <div class="grow"></div>
        <div class="ctl" style="height:30px">
          <select v-model.number="pageSize" @change="page = 1; fetchRows()">
            <option :value="50">50</option>
            <option :value="100">100</option>
            <option :value="200">200</option>
          </select>
        </div>
        <span class="mut">per page</span>
        <button class="pbtn" type="button" :disabled="page <= 1" @click="page--; fetchRows()">‹</button>
        <button class="pbtn on" type="button">{{ page }}</button>
        <button class="pbtn" type="button" :disabled="page * pageSize >= total" @click="page++; fetchRows()">›</button>
      </div>
    </div>

    <div v-if="pop" ref="lossPop" class="pop" :style="popStyle" @mouseenter="keepPop" @mouseleave="hidePopSoon">
      <h4>{{ pop.name }}</h4>
      <div class="psub">Loss breakdown</div>
      <div v-if="!popGroups.length" class="psub">No loss reasons in range</div>
      <div v-for="g in popGroups" :key="g.label">
        <div class="pgrp">{{ g.label }}</div>
        <div v-for="k in g.keys" :key="k.key" class="prow2">
          <span>{{ k.label }}</span><span>{{ fmtNum(pop[k.key]) }}</span>
        </div>
      </div>
    </div>

    <div style="margin-top:16px">
      <button v-if="userHasPermission('smartlinks.edit')" type="button" class="btn primary" @click="newCampFormDisplay = !newCampFormDisplay">New Smart Link</button>
      <NewCampaign v-show="newCampFormDisplay" :isVta="false" @close="newCampFormDisplay = false" />
    </div>
  </div>
</template>

<script>
import NewCampaign from '../components/v2/NewCampaign.smartlinksV2.vue'
import DateRangePicker from 'vue2-daterange-picker'
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
import SlV2Status from '../components/v2/SlV2Status.vue'
import { lostClass, fmtNum, fmtPct, visibleLossGroups, placeLossPop } from '../v2/status'
import moment from 'moment'
import '../v2/sl-v2.css'

export default {
  components: { NewCampaign, DateRangePicker, SlV2Status },
  data() {
    const yesterday = new Date()
    yesterday.setDate(yesterday.getDate() - 1)
    const today = new Date()
    return {
      loading: false,
      q: '',
      status: '',
      groupId: '',
      showZeros: false,
      sort: 'id',
      dir: 'desc',
      page: 1,
      pageSize: 50,
      total: 0,
      rows: [],
      summary: {},
      groupList: [{ id: 0, name: 'none' }],
      filterGroups: [{ id: '', name: 'All groups' }],
      open: {},
      pop: null,
      popStyle: {},
      popAnchor: null,
      hidePopTimer: null,
      newCampFormDisplay: false,
      searchTimer: null,
      loadedDateKey: '',
      dateRange: { startDate: yesterday, endDate: yesterday },
      maxDate: new Date(),
      ranges: {
        Today: [today, today],
        Yesterday: [yesterday, yesterday],
        'Last 7 days': [new Date(Date.now() - 24 * 7 * 3600 * 1000), yesterday]
      }
    }
  },
  computed: {
    popGroups() {
      return visibleLossGroups(this.pop)
    }
  },
  async created() {
    await this.loadGroups()
    await this.fetchRows()
  },
  methods: {
    fmtNum, fmtPct, lostClass,
    statusModel(row) {
      return {
        ...row,
        group_forced: row.forced,
        snapshot_placements: row.snapshot_placements,
        active_offers: row.active_offers
      }
    },
    onSearch() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.page = 1
        this.fetchRows()
      }, 300)
    },
    sortBy(field) {
      if (this.sort === field) this.dir = this.dir === 'asc' ? 'desc' : 'asc'
      else { this.sort = field; this.dir = field === 'id' ? 'desc' : 'desc' }
      this.fetchRows()
    },
    toggle(id) {
      this.$set(this.open, id, !this.open[id])
    },
    openWorkspace(id) {
      this.$router.push({ name: 'SmartLinksV2Workspace', params: { campId: id } })
    },
    keepPop() {
      clearTimeout(this.hidePopTimer)
    },
    hidePopSoon() {
      clearTimeout(this.hidePopTimer)
      this.hidePopTimer = setTimeout(() => { this.pop = null; this.popAnchor = null }, 120)
    },
    showPop(e, row) {
      this.keepPop()
      this.pop = row
      this.popAnchor = e.currentTarget || e.target
      this.popStyle = { position: 'fixed', visibility: 'hidden', top: '0px', left: '0px' }
      this.$nextTick(() => {
        if (!this.pop || !this.popAnchor) return
        this.popStyle = placeLossPop(this.popAnchor, this.$refs.lossPop)
      })
    },
    dateKey() {
      return moment(this.dateRange.startDate).format('YYYY-MM-DD') + ':' + moment(this.dateRange.endDate).format('YYYY-MM-DD')
    },
    onDateToggle(open) {
      if (open) return
      this.loading = true
      this.pop = null
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.dateKey() === this.loadedDateKey) {
            this.loading = false
            return
          }
          this.fetchRows()
        }, 50)
      })
    },
    async loadGroups() {
      try {
        const r = await this.$http.get(this.resources.SmartLinksCampaigns.v2Groups)
        const data = await r.json()
        this.groupList = data
        this.filterGroups = [{ id: '', name: 'All groups' }].concat(data)
      } catch (e) {
        this.notifyError('Could not fetch groups.')
      }
    },
    async fetchRows() {
      this.loading = true
      this.pop = null
      await this.$nextTick()
      try {
        const params = {
          q: this.q,
          from: moment(this.dateRange.startDate).format('YYYY-MM-DD'),
          to: moment(this.dateRange.endDate).format('YYYY-MM-DD'),
          status: this.status,
          show_zero_rows: this.showZeros,
          sort: this.sort,
          dir: this.dir,
          page: this.page,
          page_size: this.pageSize
        }
        if (this.groupId !== '' && this.groupId !== null) params.group_id = this.groupId
        const r = await this.$http.get(this.resources.SmartLinksCampaigns.v2Rows, { params })
        const data = await r.json()
        this.rows = data.rows || []
        this.summary = data.summary || {}
        this.total = data.total || 0
        this.loadedDateKey = this.dateKey()
      } catch (e) {
        this.notifyError('Could not load smart links')
      }
      this.loading = false
    },
    async generateReport(id, type) {
      try {
        const res = await this.$http.post(this.resources.SmartLinksCampaigns.newSlRequestsBlockedReport(id), undefined, {
          params: {
            date: moment(this.dateRange.startDate).format('YYYY-MM-DD'),
            end_date: moment(this.dateRange.endDate).format('YYYY-MM-DD'),
            request: type === 'requests' ? 'true' : 'false'
          }
        })
        if (res.ok) this.notifySuccess('The report will be sent to your email when ready', 10000)
        else this.notifyError('Could not generate report')
      } catch (e) {
        this.notifyError('Could not generate report')
      }
    },
    async setGroup(row, e) {
      const groupId = Number(e.target.value)
      try {
        await this.$http.put(this.resources.SmartLinksCampaigns.setCampaignGroup(row.id), { group_id: groupId })
        row.group_id = groupId
        row.forced = false
        this.notifySuccess(`Campaign ${row.name} group changed.`)
        this.fetchRows()
      } catch (err) {
        this.notifyError(`Campaign ${row.name} group didn't change.`)
      }
    }
  }
}
</script>
